import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { PrimaryHeader, PrimaryHeaderDataProps } from './primaryHeader/PrimaryHeader';
import { SecondaryHeader, SecondaryHeaderDataProps } from './secondaryHeader/SecondaryHeader';
import { AnimationDirection, AnimationDirectionType } from './menuAnimations.util';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { MontiAnchor } from './MontiAnchor';
import { ErrorBoundary } from '../../../errorHandling/ErrorBoundary';

const animationDuration = 200;

const getStyle = () => {
  return StyleSheet.create({
    header: {
      position: 'fixed',
      width: '100%',
      zIndex: 1500,
      top: 0,
      transform: 'translate3d(0, 0, 0)',
      transition: `${animationDuration}ms`,
    },
    foldingUp: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`,
      },
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        transform: 'translateY(0)',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        transform: 'translateY(0)',
      },
    },
  });
};

export interface HeaderLinksDataProps {
  href: string,
  text: string,
}

export interface HeaderDataProps {
  primaryHeader: PrimaryHeaderDataProps,
  secondaryHeader: SecondaryHeaderDataProps | null,
}

export interface HeaderDataState {
  menuFirstFolded: boolean
  menuFoldState: AnimationDirectionType
}

export class Header extends React.Component<HeaderDataProps, HeaderDataState> {
  scrollTicking: boolean = false;

  firstMenuFoldThreshold = 640;

  secondMenuFoldThreshold = 100;

  // eslint-disable-next-line react/state-in-constructor
  state = {
    menuFirstFolded: false,
    menuFoldState: AnimationDirection.NONE,
  };

  componentDidMount() {
    window.addEventListener('scroll', this.scrollHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.scrollHandler);
  }

  getMenuAnimation = (originalScrollPosition: number): AnimationDirectionType => {
    const { menuFirstFolded, menuFoldState } = this.state;
    const currentScrollPosition = window.scrollY;
    const deltaY = currentScrollPosition - originalScrollPosition;

    if (currentScrollPosition > this.firstMenuFoldThreshold && !menuFirstFolded) {
      this.setState(() => ({ menuFirstFolded: true }));
      return AnimationDirection.UP;
    }

    if (this.isScrollingUp(deltaY)) {
      return AnimationDirection.DOWN;
    }

    if (this.isScrollingDown(deltaY) && menuFirstFolded && currentScrollPosition > this.secondMenuFoldThreshold) {
      return AnimationDirection.UP;
    }

    return menuFoldState;
  };

  getHeaderStyleWithAnimation() {
    const { menuFoldState } = this.state;
    const style = getStyle();

    if (menuFoldState === AnimationDirection.NONE) {
      return css(style.header);
    }

    const animationStyle = menuFoldState === AnimationDirection.UP ? style.foldingUp : style.foldingDown;
    return css(style.header, animationStyle);
  }

  scrollHandler = () => {
    if (!this.scrollTicking) {
      const originalY = window.scrollY;
      setTimeout(() => {
        const { menuFoldState } = this.state;
        const menuAnimation = this.getMenuAnimation(originalY);
        if (menuAnimation !== menuFoldState) {
          this.setState(() => ({ menuFoldState: menuAnimation }));
        }
        this.scrollTicking = false;
      }, 100);
    }

    this.scrollTicking = true;
  };

  isScrollingUp(deltaY: number) {
    return deltaY < 0;
  }

  isScrollingDown(deltaY: number) {
    return deltaY > 0;
  }

  generateSecondaryHeader(secondaryHeader: SecondaryHeaderDataProps) {
    if (!secondaryHeader) {
      return null;
    }
    const { menuFoldState } = this.state;
    return (
      <SecondaryHeader
        moreLinksText={secondaryHeader.moreLinksText}
        secondaryLinks={secondaryHeader.secondaryLinks}
        secondaryLogos={secondaryHeader.secondaryLogos}
        animation={menuFoldState}
        animationDuration={animationDuration}
        pagePath={secondaryHeader.pagePath}
      />
    );
  }

  render() {
    const { primaryHeader, secondaryHeader } = this.props;
    const { primaryLinks, primaryLogos, editions, cta, menu, videoLogo, pagePath, editionEndpoint } = primaryHeader;
    const { menuFoldState } = this.state;

    return (
      <ErrorBoundary>
        <MontiAnchor isSecondaryHeaderEnabled={!!secondaryHeader} menuAnimation={menuFoldState} />
        <header className={this.getHeaderStyleWithAnimation()}>
          <PrimaryHeader primaryLinks={primaryLinks} primaryLogos={primaryLogos} editions={editions} cta={cta} menu={menu} videoLogo={videoLogo} pagePath={pagePath} editionEndpoint={editionEndpoint} />
          {this.generateSecondaryHeader(secondaryHeader as SecondaryHeaderDataProps)}
        </header>
      </ErrorBoundary>
    );
  }
}
